<template>
  <v-card v-if="currStep !== 0">
    <!-- Stepper Header -->
    <div class="stepper-header">
      <v-icon
        v-if="currStep === 3"
        color="#047205">done</v-icon>
      <v-icon
        v-if="currStep === 4"
        color="#DF0101">warning</v-icon>
      <div
        v-if="currStep <= 4"
        :class="getStepperTitleClass()">
        {{ getCurrTitle() }}
      </div>
    </div>

    <!-- Stepper Item -->
    <div
      v-if="currStep <= 3"
      class="stepper-wrapper">
      <div
        v-for="step in Object.keys(stepperItems)"
        :key="step"
        :class="getStepperItemClass(parseInt(step, 10))">
        <div class="step-counter">
          <div v-if="currStep > step">✓</div>
        </div>
        <div class="step-name">{{ stepperItems[step].name }}</div>
        <img
          :src="stepperItems[step].icon"
          :alt="`step${step}_icon`"
          class="stepper-icon">
        <div
          class="stepper-info"
          v-html="stepperItems[step].info"/>
      </div>
    </div>

    <!-- Stepper Note -->
    <div
      :class="getStepperNoteClass()">
      <div
        v-html="getCurrNote()"/>
      <!-- Stepper Button -->
      <div v-if="currStep === 1">
        <v-btn
          :loading="loading"
          class="stepper-btn"
          block
          dark
          color="#BF0000"
          @click="startInspection"
        >
          <div class="stepper-btn-text-continue">
            検査を行う<br>
            撮影の準備をお願いします
          </div>
        </v-btn>
        <v-btn
          block
          outline
          dark
          color="#ccc"
          @click="postponeInspection"
        >
          <div class="stepper-btn-text-cancel">
            後で行う
          </div>
        </v-btn>
      </div>
    </div>
  </v-card>
</template>

<script>
import inspectionStep1Icon from '../assets/photos/PHOTO_INSPECTION_STEP1.png';
import inspectionStep2Icon from '../assets/photos/PHOTO_INSPECTION_STEP2.png';
import inspectionStep3Icon from '../assets/photos/PHOTO_INSPECTION_STEP3.png';

const STEPPER_DETAILS = {
  1: { title: 'AI検査を行う', note: 'AI検査は一度しかできません。通信環境が良い場所で行ってください' },
  2: { title: 'データを処理しています', note: '※8-15分以上処理に時間がかかるようであればご自身で写真をご準備ください。' },
  3: {
    title: 'AI検査が完了しました',
    note: 'AI検査の結果が「写真」・「破損」に反映されております。ご確認いただき、問題があれば修正をお願いいたします。',
  },
  4: {
    title: 'AI検査に失敗しました',
    note: '診断中にエラーが発生し、AI検査が失敗しました（エラーコード：ERROR_CODE）。<br>「写真」・「破損」を手動で入力いただく必要があります。',
  },
  5: {
    title: '',
    note: '撮影の準備が出来次第、このボタンからAI検査を開始してください',
  },
};

const STEPPER_ITEMS = {
  1: {
    name: '画像診断',
    icon: inspectionStep1Icon,
    info: '各セクションごとに写真撮影を行います',
  },
  2: {
    name: '結果を待つ',
    icon: inspectionStep2Icon,
    info: '処理に<strong>8-15分</strong>程度かかります',
  },
  3: {
    name: '完了',
    icon: inspectionStep3Icon,
    info: '画像が「写真」「破損」に反映されます',
  },
};

export default {
  props: {
    currStep: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    stepperItems() {
      return STEPPER_ITEMS;
    },
    stepperDetails() {
      return STEPPER_DETAILS;
    },
    errorCode() {
      return this.$store.state.aiInspectionErrorCode;
    },
  },
  methods: {
    getStepperTitleClass() {
      return {
        'stepper-title': true,
        finished: this.currStep === 3,
        failed: this.currStep === 4,
      };
    },
    getStepperItemClass(step) {
      return {
        'stepper-item': true,
        completed: this.currStep > step,
        active: this.currStep === step,
      };
    },
    getStepperNoteClass() {
      return {
        'stepper-note': true,
        waiting: this.currStep === 2,
        finished: this.currStep === 3,
        postponed: this.currStep === 5,
      };
    },
    getCurrTitle() {
      return this.stepperDetails[this.currStep].title;
    },
    getCurrNote() {
      return this.stepperDetails[this.currStep].note.replace('ERROR_CODE', this.errorCode);
    },
    startInspection() {
      this.loading = true;
      this.$emit('start-inspection');
    },
    postponeInspection() {
      this.$emit('postpone-inspection');
    },
  },
};
</script>

<style scoped>
.stepper-wrapper {
  margin-top: auto;
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}
.stepper-item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;

  @media (max-width: 768px) {
    font-size: 12px;
  }
}

.stepper-item::before {
  position: absolute;
  content: "";
  border-bottom: 2px solid #ccc;
  width: 100%;
  top: 12px;
  left: -50%;
  z-index: 2;
}

.stepper-item::after {
  position: absolute;
  content: "";
  border-bottom: 2px solid #ccc;
  width: 100%;
  top: 12px;
  left: 50%;
  z-index: 2;
}

.stepper-item .step-counter {
  position: relative;
  z-index: 5;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px; /* Define the diameter*/
  height: 24px; /* Define the diameter*/
  border-radius: 50%;
  border: 3px solid #d3d3d3; /* Define the circle border */
  background: white;
  margin-bottom: 6px;
}

.stepper-item.active, .stepper-item.completed {
  font-weight: bold;
  color: #BF0000
}

.stepper-item.completed .step-counter {
  border: 2px solid #BF0000; /* Define the circle border */
  background: #BF0000;
  color: white;
}

.stepper-item.active .step-counter {
  border: 7px solid #BF0000; /* Define the circle border */
}


.stepper-item.completed::after {
  position: absolute;
  content: "";
  border-bottom: 2px solid #BF0000;
  width: 100%;
  top: 12px;
  left: 50%;
  z-index: 3;
}

.stepper-item:first-child::before {
  content: none;
}
.stepper-item:last-child:after {
  content: none;
}

.stepper-note {
  color: #DF0101;
  font-size: 12px;
  padding: 10px;
}

.stepper-note.waiting {
  font-weight: bold;
}

.stepper-note.finished {
  color: #333333;
  padding-bottom: 30px;
}

.stepper-note.postponed {
  color: #333333;
}

.stepper-header {
  display: flex;
  justify-content: center;
  align-items: center;
}

.stepper-title {
  text-align: center;
  padding-left: 10px;
  padding-right: 10px;
  padding-top: 20px;
  padding-bottom: 20px;
  font-size: 16px;
  font-weight: bold;
}

.stepper-title.finished {
  color: #047205;
}

.stepper-title.failed {
  color: #DF0101;
}

.stepper-icon {
  width: 80px;
  height: 45px;
  margin-top: 10px;
}

.stepper-info{
  font-weight: normal;
  font-size: 10px;
  margin-left: 10px;
  margin-right: 10px;
  color: #333333 !important;
}

.stepper-btn {
  width: 100%;
  height: 55px;
  margin-top: 20px;
}

.stepper-btn-text-continue {
  font-size: 14px;
}

.stepper-btn-text-cancel {
  font-size: 14px;
  color: #333333
}
</style>



// WEBPACK FOOTER //
// src/components/InspectionStatus.vue